/**
 * LE PAS DE TEMPS, CARTE PAR CARTE (DEV-1508 lot 6b, points R1 et R7).
 * ============================================================================
 *
 * MOT POUR MOT, la revue du 17/09 :
 *
 *  • R1 — « On voit rien il faut mieux pouvoir switcher entre données horaires
 *    et journalières quand la période le permet (suffisamment petite) », et
 *    « si la période le permet il faut pouvoir switcher sur l'affichage de la
 *    CDC » ;
 *  • R7 — « Et idem sur les sélections de pas de temps que pour les C5 », sur
 *    les trois cartes de puissance d'un compteur fin.
 *
 * CE QUI EXISTAIT, ET POURQUOI ÇA NE SUFFIT PAS. Le niveau se déduit de la
 * LARGEUR regardée et d'elle seule (`niveauPourFenetre` : natif ≤ 14 j, horaire
 * de 15 à 92, journalier au-delà), pour TOUT l'onglet à la fois. Sur trois mois,
 * la seule façon de voir une courbe de charge était de resserrer la fenêtre à
 * deux semaines — donc de perdre de vue les trois mois. Le pas devient ici un
 * choix de LECTURE, par carte, qui ne touche pas la fenêtre regardée.
 *
 * LES TROIS RÈGLES QUI TIENNENT TOUT
 * ----------------------------------
 *
 * 1. AUCUN SEUIL INVENTÉ. Un pas n'est offert que si deux faits le permettent,
 *    et ces deux faits sont LUS :
 *
 *      • ce compteur DÉTIENT cette variable à ce grain (`variables_datav2`,
 *        recopié dans `SerieOnglet.grains`) ;
 *      • la fenêtre REGARDÉE tient sous le plafond dapi de ce grain
 *        (`plafondJours`, repris du lecteur — `services/sourceDataEnedis`).
 *
 *    La table qui en résulte, pour une fenêtre regardée de `j` jours :
 *
 *      | Pas offert            | Grain  | Plafond dapi | Offert si       |
 *      |-----------------------|--------|--------------|-----------------|
 *      | Courbe de charge      | natif  | 31 j         | j ≤ 31          |
 *      | Horaire               | hourly | 366 j        | j ≤ 366         |
 *      | Journalier            | daily  | 730 j        | j ≤ 730         |
 *      | Journalier (énergie,  | daily  | 1 095 j      | j ≤ 1 095       |
 *      |  index, dépassements) |        |              |                 |
 *
 *    Le plafond long du journalier vient de `PREFIXES_FENETRE_LONGUE` : il ne
 *    se décide pas ici, il se lit là-bas.
 *
 * 2. UN CHANGEMENT DE PAS COÛTE UN APPEL, PAS UN RECHARGEMENT. C'est la raison
 *    d'être du plafond comme critère : sous le plafond, la fenêtre part en UN
 *    appel par sens (`decoupePourNiveau` ne la découpe pas), et la carte se
 *    recharge seule, sous sa propre clé de mémoire. Rien d'autre sur la page ne
 *    bouge — ni la fenêtre, ni la bande, ni les autres cartes.
 *
 * 3. LE DÉFAUT EST LE PAS DE L'ONGLET. Tant que personne n'a choisi, la carte
 *    est exactement celle d'avant : c'est l'utilisateur qui affine, la page ne
 *    décide pas pour lui d'aller au plus fin.
 *
 * ET QUAND LA FENÊTRE CHANGE. Le choix SURVIT au cliquer-glisser tant qu'il
 * reste permis. Dès qu'il ne l'est plus — on a élargi au-delà du plafond du
 * grain, ou ce grain n'est plus détenu — la carte retombe sur le pas permis le
 * plus proche et DIT POURQUOI, dans la carte. Jamais de bannière (règle de
 * Mathieu du 08/09), jamais un pas qui change en silence.
 */
import {
    GRAINS_APPARENTE,
    plafondJours,
    type GrainSeries,
} from "@modules/data/services/sourceDataEnedis.service"
import type { GrapheEnedis } from "@modules/data/utils/graphesEnedis"
import type {
    OngletResolu,
    SerieOnglet,
} from "@modules/data/utils/ongletsEnedis"

// ============================================================================
// LES PAS OFFERTS
// ============================================================================

/**
 * Les trois pas que la carte propose, DU PLUS GROSSIER AU PLUS FIN.
 *
 * Les autres grains servis (`weekly`, `monthly`, `periode`) n'en sont pas : ils
 * ne se choisissent pas, ils sont la seule résolution de ce que la carte porte
 * (une période de facturation n'a pas de version horaire). Une carte servie à
 * l'un d'eux n'offre donc aucun sélecteur, plutôt qu'un sélecteur à une entrée.
 */
export const PAS_OFFERTS: GrainSeries[] = ["daily", "hourly", "natif"]

/** Les mots du client. « Courbe de charge » n'est jamais dit « natif ». */
export const LIBELLES_PAS: Partial<Record<GrainSeries, string>> = {
    daily: "Journalier",
    hourly: "Horaire",
    natif: "Courbe de charge",
}

/** Le libellé d'un pas, ou son code si le vocabulaire ne le connaît pas. */
export function libelleDuPas(pas: GrainSeries): string {
    return LIBELLES_PAS[pas] ?? pas
}

// ============================================================================
// L'IDENTITÉ D'UNE CARTE — STABLE D'UN PAS À L'AUTRE
// ============================================================================

/**
 * LA CLÉ D'UNE CARTE NE PEUT PAS ÊTRE `graphe.cle` (piège relevé en écrivant ce
 * lot).
 *
 * `graphe.cle` porte l'UNITÉ, et l'unité change avec le grain : la puissance
 * active se lit en kW sous forme d'enveloppe dérivée, et en kVA quand c'est la
 * PMax relevée du jour. Une carte rangée sous sa clé aurait donc perdu son
 * choix de pas à chaque changement de pas — le choix ne survivrait ni au brush,
 * ni à lui-même.
 *
 * L'identité retenue ne tient qu'à des faits que le grain ne déplace pas : les
 * noms de variables servis, leur sens, et le fait d'être la carte de PMax
 * relevée (qui partage son nom de variable avec l'enveloppe, et n'est pas la
 * même carte).
 */
export function identiteDeLaCarte(graphe: GrapheEnedis): string {
    const pmax = graphe.series.some((s) => s.pmaxMesuree) ? "pmax|" : ""
    const noms = [
        ...new Set(graphe.series.map((s) => `${s.direction}:${s.variable}`)),
    ].sort()
    return `${pmax}${noms.join("+")}`
}

/** Les séries de l'onglet que cette carte porte, quel que soit le grain. */
export function seriesDeLaCarte(
    onglet: OngletResolu,
    graphe: GrapheEnedis
): SerieOnglet[] {
    const noms = new Set(
        graphe.series.map((s) => `${s.direction}:${s.variable}`)
    )
    return onglet.series.filter((s) => noms.has(`${s.direction}:${s.variable}`))
}

// ============================================================================
// CE QUE LA FENÊTRE PERMET
// ============================================================================

export type DemandeDePas = {
    /** Les séries de l'onglet que la carte porte. */
    series: SerieOnglet[]
    /** La largeur REGARDÉE, en jours. `null` = inconnue : rien n'est offert. */
    joursRegardes: number | null
}

/** Les noms de variables de la carte, sans doublon. */
function variablesDe(series: SerieOnglet[]): string[] {
    return [...new Set(series.map((s) => s.variable))]
}

/** Les grains que ce compteur DÉTIENT pour au moins une série de la carte. */
export function grainsDetenus(series: SerieOnglet[]): GrainSeries[] {
    const detenus = new Set(series.flatMap((s) => s.grains))
    return PAS_OFFERTS.filter((g) => detenus.has(g))
}

/**
 * LES PAS RÉELLEMENT OFFERTS SUR CETTE CARTE, du plus grossier au plus fin.
 *
 * Trois filtres, tous trois faits d'un fait servi :
 *
 *  • le compteur détient ce grain pour cette carte ;
 *  • la puissance apparente n'existe qu'aux grains fins (`GRAINS_APPARENTE`,
 *    règle du lecteur) : une carte qui ne porte QUE de l'apparente n'offre pas
 *    le journalier, où elle reviendrait vide ;
 *  • la fenêtre regardée tient sous le plafond dapi du grain.
 *
 * Une liste à un seul élément est rendue telle quelle : c'est à l'écran de
 * décider qu'un choix unique ne se montre pas comme un choix.
 */
export function pasPermis(demande: DemandeDePas): GrainSeries[] {
    const jours = demande.joursRegardes
    if (typeof jours !== "number" || !Number.isFinite(jours) || jours <= 0) {
        return []
    }
    const variables = variablesDe(demande.series)
    if (variables.length === 0) return []
    return grainsDetenus(demande.series).filter((grain) => {
        // La carte doit garder au moins une variable demandable à ce grain.
        const demandables = variables.filter(
            (v) =>
                GRAINS_APPARENTE.includes(grain) ||
                !v.startsWith("puissanceapparente")
        )
        if (demandables.length === 0) return false
        const plafond = plafondJours(grain, demandables)
        return plafond === null || jours <= plafond
    })
}

/**
 * LE PAS PERMIS LE PLUS PROCHE de celui qu'on voulait, ou `null` quand rien
 * n'est permis.
 *
 * « Le plus proche » se compte sur l'échelle des pas offerts, et un écart égal
 * va au PLUS GROSSIER : on arrive presque toujours ici en ayant ÉLARGI la
 * fenêtre, et c'est le grossier qui tient sur une fenêtre large.
 */
export function pasLePlusProche(
    voulu: GrainSeries,
    permis: GrainSeries[]
): GrainSeries | null {
    if (permis.length === 0) return null
    if (permis.includes(voulu)) return voulu
    const rang = PAS_OFFERTS.indexOf(voulu)
    if (rang < 0) return permis[0]
    return [...permis].sort((a, b) => {
        const da = Math.abs(PAS_OFFERTS.indexOf(a) - rang)
        const db = Math.abs(PAS_OFFERTS.indexOf(b) - rang)
        if (da !== db) return da - db
        return PAS_OFFERTS.indexOf(a) - PAS_OFFERTS.indexOf(b)
    })[0]
}

/**
 * POURQUOI LE PAS VOULU N'EST PAS SERVI, dit avec les faits qui l'ont refusé.
 *
 * Deux causes, et elles ne se confondent pas : la fenêtre est plus large que ce
 * que le lecteur sert d'un coup à ce pas, ou ce compteur ne détient rien à ce
 * pas pour cette grandeur. Écrire la première sur la seconde laisserait croire
 * qu'un recadrage suffirait à la ramener.
 *
 * `null` quand le pas voulu EST servi : il n'y a alors rien à expliquer.
 */
export function raisonDuRepli(params: {
    voulu: GrainSeries
    servi: GrainSeries | null
    series: SerieOnglet[]
    joursRegardes: number | null
}): string | null {
    const { voulu, servi } = params
    if (servi === voulu) return null
    const mot = libelleDuPas(voulu).toLocaleLowerCase("fr-FR")
    const suite = servi
        ? ` Pas ${libelleDuPas(servi).toLocaleLowerCase("fr-FR")} servi.`
        : ""
    if (!grainsDetenus(params.series).includes(voulu)) {
        return `Le pas ${mot} n'est pas détenu pour cette grandeur.${suite}`
    }
    const plafond = plafondJours(voulu, variablesDe(params.series))
    const jours = params.joursRegardes
    if (plafond !== null && typeof jours === "number" && jours > plafond) {
        return `Le pas ${mot} se lit sur ${plafond} jours au plus ; la fenêtre en compte ${jours}.${suite}`
    }
    return `Le pas ${mot} n'est pas servi sur cette fenêtre.${suite}`
}
